import classNames from 'classnames';
import * as React from 'react';
import { Button } from '../Button/Button';
import { SvgIcon } from '../SvgIcon/SvgIcon';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { useIsInView } from '~/Utils/Hooks/useIsInView';
import styles from './StyledTabs.module.css';
import type { TabsProps } from './Tabs';
import { Tabs } from './Tabs';

interface StyledTabsBaseProps extends TabsProps {
    colorVariant?: 'light' | 'dark';
}

export interface ScrollableTabsTranslations {
    scrollStart: string;
    scrollEnd: string;
}

interface StyledTabsWithArrowsProps extends StyledTabsBaseProps {
    translations: ScrollableTabsTranslations;
    arrows?: true;
}

interface StyledTabsWithoutArrowsProps extends StyledTabsBaseProps {
    translations: never;
    arrows: false;
}

export type StyledTabsProps = StyledTabsWithArrowsProps | StyledTabsWithoutArrowsProps;

export const StyledTabs: React.FC<React.PropsWithChildren<StyledTabsProps>> = props => {
    const theme = useTheme();
    const {
        className,
        tabsListClassName,
        colorVariant = 'light',
        arrows = true,
        translations,
        tabs,
        TabListWrapper: ParentTabListWrapper = React.Fragment,
        ...restProps
    } = props;

    const { withRoundedCorners } = theme;

    const tabListRef = React.useRef<HTMLDivElement>(null);

    const TabListWrapper = React.useMemo<React.FC<React.PropsWithChildren>>(() => {
        if (!arrows) {
            return ParentTabListWrapper;
        }

        return tabListWrapperProps => {
            const { children } = tabListWrapperProps;

            return (
                <ParentTabListWrapper>
                    <TabListWrapperWithArrows translations={translations} tabListRef={tabListRef}>
                        {children}
                    </TabListWrapperWithArrows>
                </ParentTabListWrapper>
            );
        };
    }, [ParentTabListWrapper, translations, arrows, tabs]);

    return (
        <Tabs
            {...restProps}
            tabs={tabs.map(tab => ({
                ...tab,
                tabClassName: classNames(styles.Tab, tab.tabClassName),
                tabSelectedClassName: classNames(styles['Tab--selected'], tab.tabSelectedClassName),
            }))}
            className={classNames(styles[`Variant--${colorVariant}`], className)}
            tabsListClassName={classNames(
                styles.TabList,
                withRoundedCorners && styles['TabList--rounded'],
                tabsListClassName
            )}
            panelClassName={styles.TabPanel}
            tabListRef={tabListRef}
            TabListWrapper={TabListWrapper}
        />
    );
};

interface TabListWrapperWithArrowsProps extends Pick<StyledTabsWithArrowsProps, 'translations'> {
    tabListRef: React.RefObject<HTMLDivElement>;
}

const TabListWrapperWithArrows: React.FC<React.PropsWithChildren<TabListWrapperWithArrowsProps>> = props => {
    const { children, tabListRef, translations } = props;

    const startRef = React.useRef<Element>();
    const endRef = React.useRef<Element>();

    React.useEffect(() => {
        if (!tabListRef.current) {
            return;
        }

        const tabListChildren = Array.from(tabListRef.current.children);
        const length = tabListChildren.length;

        if (length > 0) {
            startRef.current = tabListChildren[0];
            endRef.current = tabListChildren[length - 1];
        }
    }, [tabListRef]);

    const observerOptions = {
        threshold: 0.99,
        root: tabListRef.current,
    };

    const isStartVisible = useIsInView(startRef, observerOptions);
    const isEndVisible = useIsInView(endRef, observerOptions);

    const handleScrollEnd = () => {
        if (tabListRef.current) {
            tabListRef.current.scrollLeft = tabListRef.current.scrollWidth;
        }
    };

    const handleScrollStart = () => {
        if (tabListRef.current) {
            tabListRef.current.scrollLeft = 0;
        }
    };

    const renderArrowScrollStart = () => (
        <Button
            onClick={handleScrollStart}
            type={ButtonType.Clear}
            aria-label={translations.scrollStart}
            className={classNames(styles.Arrow, styles['Arrow--prev'])}
            disabled={isStartVisible}
        >
            <SvgIcon iconName="chevron-left" className={styles.ArrowIcon} />
        </Button>
    );

    const renderArrowScrollEnd = () => (
        <Button
            onClick={handleScrollEnd}
            type={ButtonType.Clear}
            aria-label={translations.scrollEnd}
            className={classNames(styles.Arrow, styles['Arrow--next'])}
            disabled={isEndVisible}
        >
            <SvgIcon iconName="chevron-right" className={styles.ArrowIcon} />
        </Button>
    );

    return (
        <div className={styles.TabListWrapper}>
            {renderArrowScrollStart()}
            {children}
            {renderArrowScrollEnd()}
        </div>
    );
};
